import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useSession, useSignOut } from '@/features/auth/auth';
import { Button } from '@/components/ui/button';
import { Wordmark } from '@/components/Wordmark';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  managerOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: '/conversations', label: 'Conversations' },
  { to: '/customers', label: 'Customers' },
  { to: '/topics', label: 'Topics', managerOnly: true },
  { to: '/documents', label: 'Knowledge', managerOnly: true },
  { to: '/metrics', label: 'Metrics', managerOnly: true },
  { to: '/audit', label: 'Audit log', managerOnly: true },
];

export function Layout() {
  const { data: user } = useSession();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const isManager = user?.role === 'manager';
  const visibleNav = NAV.filter((n) => !n.managerOnly || isManager);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Brand accent bar */}
      <div className="h-1 bg-anchor" />
      <header className="border-b bg-card">
        <div className="container flex h-14 items-center justify-between">
          <Link to="/conversations" aria-label="Anchor Comms home">
            <Wordmark className="text-lg" />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {visibleNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  cn(
                    'px-3 py-1.5 text-sm font-semibold rounded-md transition-colors',
                    isActive
                      ? 'bg-secondary text-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                  )
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {user && (
              <span className="hidden sm:inline text-sm text-muted-foreground">
                {user.name} {isManager ? '· manager' : ''}
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                signOut.mutate(undefined, { onSuccess: () => navigate('/sign-in') })
              }
            >
              <LogOut className="h-4 w-4" />
              <span className="sr-only">Sign out</span>
            </Button>
          </div>
        </div>
        <nav className="md:hidden border-t">
          <div className="container flex gap-1 overflow-x-auto py-2 text-sm">
            {visibleNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  cn(
                    'px-2 py-1 rounded',
                    isActive ? 'bg-secondary' : 'text-muted-foreground',
                  )
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>
      <main className="flex-1 container py-6">
        <Outlet />
      </main>
    </div>
  );
}
